import React, { useState, useEffect, useContext, useMemo, lazy, Suspense, useCallback } from 'react';
import { Timestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import AuthContext from '../contexts/AuthContext';
import PostCard from '../components/posts/PostCard';
import RepostCard from '../components/posts/RepostCard';
import CreatePostForm from '../components/posts/CreatePostForm';
import SEOHead from '../components/seo/SEOHead';
import { useTranslation } from 'react-i18next';
import cacheService from '../services/cacheService';
import PostFilterSelector from '../components/posts/PostFilterSelector';
import { useFilteredPosts, useDiscoverPosts, PostFilter } from '../hooks/useFilteredPosts';
import { getPreferredPostFilter } from '../services/privacyService';

// Lazy loading de componentes menos críticos para mejorar el rendimiento inicial
const WhoToFollowSidebar = lazy(() => import('../components/sidebar/WhoToFollowSidebar'));
const TrendingHashtagsSidebar = lazy(() => import('../components/sidebar/TrendingHashtagsSidebar'));

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  commentsCount?: number;
  createdAt: Timestamp | Date | string;
  type?: 'post' | 'repost';
  originalPostId?: string;
  isPrivate?: boolean;
  hasMedia?: boolean;
}

const Home: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation();

  // Estado para el filtro de posts
  const [currentFilter, setCurrentFilter] = useState<PostFilter>('following');
  const [isInitializing, setIsInitializing] = useState(true);

  // Obtener posts filtrados usando el hook personalizado
  const { posts, loading, error } = useFilteredPosts({
    userId: currentUser?.uid,
    filter: currentFilter,
    limitCount: 20
  });

  // Obtener posts populares para descubrir
  const { posts: discoverPosts, loading: loadingDiscover } = useDiscoverPosts(currentUser?.uid, 5);

  // Estado para la paginación (reservado para futura implementación)
  // const [hasMore, setHasMore] = useState(true);
  // const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Cargar el filtro preferido del usuario al iniciar
  useEffect(() => {
    const loadPreferredFilter = async () => {
      if (currentUser?.uid && isInitializing) {
        try {
          const preferredFilter = await getPreferredPostFilter(currentUser.uid);
          setCurrentFilter(preferredFilter);
        } catch (error) {
          console.error('Error al cargar filtro preferido:', error);
        } finally {
          setIsInitializing(false);
        }
      } else if (!currentUser?.uid && isInitializing) {
        // Si no hay usuario, usar el filtro por defecto
        setCurrentFilter('recent');
        setIsInitializing(false);
      }
    };

    loadPreferredFilter();
  }, [currentUser?.uid, isInitializing]);

  // Cambiar el filtro de posts
  const handleFilterChange = useCallback((filter: PostFilter) => {
    setCurrentFilter(filter);
  }, []);

  // Manejar el estado de conexión
  const handleConnectionChange = useCallback((isOnline: boolean) => {
    if (isOnline) {
      console.log('Conexión restaurada, sincronizando datos...');
    } else {
      console.log('Sin conexión, usando datos en caché...');
    }
  }, []);

  // Configurar listener para el estado de conexión
  useEffect(() => {
    cacheService.addConnectionListener(handleConnectionChange);

    return () => {
      cacheService.removeConnectionListener(handleConnectionChange);
    };
  }, [handleConnectionChange]);

  // Componente para manejar la carga del post original en un repost
  const RepostPostWrapper = ({ post }: { post: Post }) => {
    const [originalPost, setOriginalPost] = useState<Post | null>(null);
    const [loadingOriginal, setLoadingOriginal] = useState(true);
    const [errorOriginal, setErrorOriginal] = useState<string | null>(null);

    useEffect(() => {
      const fetchOriginalPost = async () => {
        if (!post.originalPostId) {
          setLoadingOriginal(false);
          return;
        }

        try {
          const docRef = doc(db, 'posts', post.originalPostId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setOriginalPost({ id: docSnap.id, ...docSnap.data() } as Post);
          } else {
            setErrorOriginal(t('post.originalNotFound'));
          }
        } catch (err) {
          console.error('Error fetching original post:', err);
          setErrorOriginal(t('post.errorLoadingOriginal'));
        } finally {
          setLoadingOriginal(false);
        }
      };

      fetchOriginalPost();
    }, [post.originalPostId, t]);

    if (loadingOriginal) {
      return (
        <div className="animate-pulse p-4 bg-gray-100 dark:bg-gray-800 rounded-lg mt-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      );
    }

    if (errorOriginal) {
      return (
        <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg mt-2 text-sm text-gray-500 dark:text-gray-400">
          {errorOriginal}
        </div>
      );
    }

    if (!originalPost) {
      return null;
    }

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg mt-2 overflow-hidden">
        <PostCard post={originalPost} isReposted={true} />
      </div>
    );
  };



  // Renderizar los posts
  const renderPosts = useMemo(() => {
    if (loading && posts.length === 0) {
      // Mostrar skeleton loader cuando se está cargando y no hay posts
      return Array.from({ length: 3 }).map((_, index) => (
        <div key={`skeleton-${index}`} className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
          <div className="flex items-center space-x-4 mb-4">
            <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-12 w-12"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          </div>
          <div className="mt-4 flex justify-between">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
          </div>
        </div>
      ));
    }

    return posts.map((post) => {
      if (post.type === 'repost' && post.originalPostId) {
        return (
          <RepostCard key={post.id} post={post}>
            <RepostPostWrapper post={post} />
          </RepostCard>
        );
      }
      return <PostCard key={post.id} post={post} />;
    });
  }, [posts, loading]);

  // Renderizar los posts de descubrimiento
  const renderDiscoverPosts = useMemo(() => {
    if (loadingDiscover || discoverPosts.length === 0) {
      return null;
    }

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <h2 className="text-xl font-semibold mb-4">{t('home.discover')}</h2>
        <div className="space-y-4">
          {discoverPosts.map((post) => (
            <div key={post.id} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
              <PostCard post={post} isCompact={true} />
            </div>
          ))}
        </div>
      </div>
    );
  }, [discoverPosts, loadingDiscover, t]);

  return (
    <>
      <SEOHead
        title={t('home.title')}
        description={t('home.description')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-7xl mx-auto px-4 py-6">
        {/* Main content */}
        <div className="lg:col-span-7">
          {/* Formulario para crear post */}
          {currentUser && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
              <CreatePostForm />
            </div>
          )}

          {/* Selector de filtros */}
          <PostFilterSelector
            currentFilter={currentFilter}
            onFilterChange={handleFilterChange}
          />

          {/* Lista de posts */}
          {error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 text-sm text-red-600 dark:text-red-400 underline"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : loading && posts.length === 0 ? (
            <div className="flex justify-center my-8" aria-live="polite">
              <div
                className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600"
                role="status"
              >
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            </div>
          ) : posts.length > 0 ? (
            <div className="space-y-4">
              {renderPosts}

              {/* Mensaje cuando no hay más posts (deshabilitado temporalmente) */}
              {/* {!hasMore && posts.length > 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">{t('home.noMorePosts')}</p>
                </div>
              )} */}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                {currentFilter === 'following'
                  ? t('home.noFollowingPosts')
                  : t('home.noPostsToShow')}
                {currentUser && t('home.beFirstToPost')}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-5 space-y-4">
          {/* Posts de descubrimiento */}
          {currentFilter === 'following' && renderDiscoverPosts}

          {/* Sugerencias de usuarios para seguir */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h2 className="text-xl font-semibold mb-4">{t('home.whoToFollow')}</h2>
            <Suspense fallback={<div className="animate-pulse h-40 bg-gray-200 dark:bg-gray-700 rounded"></div>}>
              <WhoToFollowSidebar />
            </Suspense>
          </div>

          {/* Hashtags en tendencia */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h2 className="text-xl font-semibold mb-4">{t('home.trendingHashtags')}</h2>
            <Suspense fallback={<div className="animate-pulse h-40 bg-gray-200 dark:bg-gray-700 rounded"></div>}>
              <TrendingHashtagsSidebar />
            </Suspense>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
